import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import classNames from "classnames"
import { Drawer } from "vaul"

import PillButton from "@components/elements/V2/Buttons/PillButton"
import HtmlRichText from "@components/elements/V2/HtmlRichText"
import CloseIcon from "@components/elements/V2/Icons/CloseIcon"
import LockIcon from "@components/elements/V2/Icons/LockIcon"
import ShareIcon from "@components/elements/V2/Icons/ShareIcon"
import Typography from "@components/elements/V2/Typography"
import RichTextElement from "@components/StoryBlok/RichTextElement"
import { TabSelectorItem } from "@components/V2/Banners/TabSelector/TabSelectorItem"
import { Color } from "@constants/V2/color"
import { useScrollSpyTabs } from "@hooks/V2/useScrollSpyTabs"
import { stripHtmlTags } from "@utils/richtext"
import { getDepartmentIcon } from "@utils/V2/department"
import React, { useState } from "react"

const MIN_SECTION_TEXT_LENGTH = 5

type SectionKey =
  | "company"
  | "description"
  | "requirements"
  | "responsibilities"
  | "benefits"
  | "culture"

type DrawerSection = {
  key: SectionKey
  label: string
  wrapperClassName?: string
  content: React.ReactNode
}

type TabKey =
  | "about"
  | "role"
  | "qualification"
  | "other"
  | "team"
  | "responsibilities"

type DrawerTab = {
  key: TabKey
  label: string
  targetSectionKey: SectionKey
}

type TabDef = {
  key: TabKey
  label: string
  sectionKeys: SectionKey[]
}

const TAB_DEFS: TabDef[] = [
  { key: "about", label: "About", sectionKeys: ["company"] },
  {
    key: "team",
    label: "The Team",
    sectionKeys: ["description"],
  },
  {
    key: "role",
    label: "The Role",
    sectionKeys: ["requirements"],
  },
  {
    key: "qualification",
    label: "Qualification",
    sectionKeys: ["responsibilities"],
  },
  { key: "other", label: "Other", sectionKeys: ["benefits", "culture"] },
]

export interface JobDrawerTypes {
  companyHeader: string
  companySection: Storyblok.RichTextBlock
  descriptionHeader: string
  requirementsHeader: string
  responsibilitiesHeader: string
  benefitsHeader: string
  cultureHeader: string
  cultureSection: Storyblok.RichTextBlock
}

type Props = {
  job: HiBob.JobAd
  isOpen: boolean
  onClose: () => void
  drawerData: JobDrawerTypes
}

const JobDrawer = ({ job, isOpen, onClose, drawerData }: Props) => {
  const [copied, setCopied] = useState(false)

  const DepartmentIcon = job.department
    ? getDepartmentIcon(job.department)
    : null

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text)
    setCopied(true)

    setTimeout(() => setCopied(false), 2000)
  }

  const sections: DrawerSection[] = []
  const wrapperClassName = "text-responsive-body-sm pb-20"

  if (drawerData.companyHeader && drawerData.companySection) {
    sections.push({
      key: "company",
      label: drawerData.companyHeader,
      wrapperClassName: classNames("mt-20 !pb-0", wrapperClassName),
      content: <RichTextElement content={drawerData.companySection} />,
    })
  }

  if (
    job.description &&
    stripHtmlTags(job.description).length >= MIN_SECTION_TEXT_LENGTH
  ) {
    sections.push({
      key: "description",
      label: drawerData.descriptionHeader,
      wrapperClassName: wrapperClassName,
      content: <HtmlRichText content={job.description} />,
    })
  }

  if (
    job.requirements &&
    stripHtmlTags(job.requirements).length >= MIN_SECTION_TEXT_LENGTH
  ) {
    sections.push({
      key: "requirements",
      label: drawerData.requirementsHeader,
      wrapperClassName: wrapperClassName,
      content: <HtmlRichText content={job.requirements} coloredListMarkers />,
    })
  }

  if (
    job.responsibilities &&
    stripHtmlTags(job.responsibilities).length >= MIN_SECTION_TEXT_LENGTH
  ) {
    sections.push({
      key: "responsibilities",
      label: drawerData.responsibilitiesHeader,
      wrapperClassName: classNames("pb-10", wrapperClassName),
      content: (
        <HtmlRichText content={job.responsibilities} coloredListMarkers />
      ),
    })
  }

  if (
    job.benefits &&
    stripHtmlTags(job.benefits).length >= MIN_SECTION_TEXT_LENGTH
  ) {
    sections.push({
      key: "benefits",
      label: drawerData.benefitsHeader,
      wrapperClassName: classNames(wrapperClassName),
      content: <HtmlRichText content={job.benefits} coloredListMarkers />,
    })
  }

  if (drawerData.cultureHeader && drawerData.cultureSection) {
    sections.push({
      key: "culture",
      label: drawerData.cultureHeader,
      wrapperClassName: wrapperClassName,
      content: <RichTextElement content={drawerData.cultureSection} />,
    })
  }

  const {
    scrollContainerRef,
    activeTabKey,
    scrollToSection,
    registerSectionRef,
  } = useScrollSpyTabs(
    TAB_DEFS,
    sections.map((section) => section.key)
  )

  const tabs: DrawerTab[] = []

  for (const def of TAB_DEFS) {
    const matchingSection = sections.find((section) =>
      def.sectionKeys.includes(section.key)
    )
    if (matchingSection) {
      tabs.push({
        key: def.key,
        label: def.label,
        targetSectionKey: matchingSection.key,
      })
    }
  }

  const handleTabClick = (tab: DrawerTab) => {
    scrollToSection(tab.key, tab.targetSectionKey)
  }

  return (
    <Drawer.Root
      open={isOpen}
      direction="right"
      onOpenChange={(open) => !open && onClose()}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/30" />

        <VisuallyHidden>
          <Drawer.Description>Job details for {job.title}</Drawer.Description>
        </VisuallyHidden>

        <Drawer.Content className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[540px] flex-col bg-white shadow-xl">
          {/* Top bar: badges + close */}

          <Drawer.Close className="absolute right-24 top-24 shrink-0 rounded-12 bg-light-gray p-8">
            <CloseIcon
              strokeColor={Color.Black}
              width="24px"
              height="24px"
              strokeWidth="2"
            />
          </Drawer.Close>

          {/* Title + metadata */}
          <div className="mt-80 shrink-0 px-40">
            <Drawer.Title asChild>
              <Typography
                element="h2"
                size="lead-md"
                font="grotesk"
                weight="medium"
                color={Color.Charcoal}
                text={job.title}
                className="mb-12"
              />
            </Drawer.Title>
          </div>

          {/* Section tabs */}
          {tabs.length > 0 && (
            <div className="flex shrink-0 gap-24 overflow-x-auto border-b border-gray px-40">
              {tabs.map((tab) => (
                <TabSelectorItem
                  key={tab.key}
                  title={tab.label}
                  isSelected={tab.key === activeTabKey}
                  textColor={Color.Charcoal}
                  variant="underline"
                  onClick={() => handleTabClick(tab)}
                />
              ))}
            </div>
          )}

          {/* Scrollable body */}
          <div
            ref={scrollContainerRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto px-40 pb-20"
          >
            <div className="mb-4 mt-[30px] flex flex-wrap gap-16">
              {job.department && DepartmentIcon && (
                <div className="flex w-fit items-center gap-6 rounded-8 border border-gray px-10 py-6">
                  <DepartmentIcon className="size-20 shrink-0 text-coal" />
                  <Typography
                    size="body-sm"
                    font="grotesk"
                    weight="book"
                    color={Color.Coal}
                    text={job.department}
                  />
                </div>
              )}
              {job.site && (
                <div className="flex items-center gap-4">
                  <Typography
                    size="body-sm"
                    font="grotesk"
                    weight="medium"
                    color={Color.Coal}
                    text={job.site}
                  />
                </div>
              )}
              {job.employmentType && (
                <div className="flex items-center gap-4">
                  <Typography
                    size="body-sm"
                    font="grotesk"
                    weight="medium"
                    color={Color.Coal}
                    text={job.employmentType}
                  />
                </div>
              )}
            </div>

            {sections.map((section) => (
              <div
                key={section.key}
                ref={registerSectionRef(section.key)}
                className={classNames("scroll-mt-8", section.wrapperClassName)}
              >
                <Typography
                  element="h3"
                  size="body-lg"
                  font="grotesk"
                  weight="medium"
                  color={Color.Charcoal}
                  text={section.label}
                  className="mb-8"
                />
                {section.content}
              </div>
            ))}
          </div>

          {/* Footer: apply CTA + secure note */}
          <div className="shrink-0 border-t border-gray px-24 py-20">
            <div className="flex flex-col gap-10 sm:flex-row">
              {job.applyUrl && (
                <PillButton
                  style="solid"
                  size="medium"
                  color={Color.Blue}
                  text="Apply for this role"
                  linkUrl={{ url: job.applyUrl, target: "_blank" }}
                  className="sm:mb-16"
                />
              )}

              {job.applyUrl && (
                <PillButton
                  style="bordered"
                  size="medium"
                  overrideTextColor={Color.Charcoal}
                  color={Color.Blue}
                  icon={<ShareIcon className="h-16" />}
                  iconPosition="left"
                  text={copied ? "Copied!" : "Share"}
                  className="mb-16"
                  onClick={() => handleCopy(job.applyUrl)}
                />
              )}
            </div>

            <div className="flex items-center gap-6">
              <LockIcon className="shrink-0 text-coal" />
              <Typography
                size="subscript-md"
                font="grotesk"
                weight="book"
                color={Color.Coal}
                text="Secure — applications are handled on Yoco's hiring platform."
              />
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

export default JobDrawer
